import type { CsvParseError, MassMigrationOrgType, ParsedCsvResult } from '@/types/massMigration';

// Shared by UploadPreviewA and UploadPreviewB so both previews label a given CSV
// column the same way. Keys are the snake_case header names the backend's
// CsvValidationService validates against and reports errors for.

export const NEW_ORG_COLUMNS = [
  'bridge_id',
  'org_name',
  'street',
  'city',
  'state',
  'zip',
  'owner_first',
  'owner_last',
  'owner_email',
  'owner_phone',
  'billing',
  'send_invite',
  // Required for operators, optional for admins (admins may leave the cells empty).
  'euid',
] as const;

export const EXISTING_ORG_COLUMNS = ['bridge_id', 'amplify_org_uuid'] as const;

export type CsvColumnKey =
  | (typeof NEW_ORG_COLUMNS)[number]
  | (typeof EXISTING_ORG_COLUMNS)[number];

/** Maps snake_case CSV column keys to the camelCase ParsedCsvRow property names. */
export const COLUMN_TO_ROW_PROP: Record<CsvColumnKey, keyof ParsedCsvResult['rows'][number]> = {
  bridge_id: 'bridgeId',
  org_name: 'orgName',
  street: 'street',
  city: 'city',
  state: 'state',
  zip: 'zip',
  owner_first: 'ownerFirstName',
  owner_last: 'ownerLastName',
  owner_email: 'ownerEmail',
  owner_phone: 'ownerPhone',
  billing: 'billing',
  send_invite: 'sendInvite',
  euid: 'euid',
  amplify_org_uuid: 'amplifyOrgUuid',
};

/** Human-readable display labels for each CSV column key. */
export const COLUMN_DISPLAY_NAMES: Record<CsvColumnKey, string> = {
  bridge_id: 'Bridge ID',
  org_name: 'Org Name',
  street: 'Street',
  city: 'City',
  state: 'State',
  zip: 'Zip',
  owner_first: 'Owner First',
  owner_last: 'Owner Last',
  owner_email: 'Owner Email',
  owner_phone: 'Owner Phone',
  billing: 'Billing',
  send_invite: 'Send Invite',
  euid: 'EUID',
  amplify_org_uuid: 'Amplify Org UUID',
};

export function getColumns(orgType: MassMigrationOrgType): readonly CsvColumnKey[] {
  return orgType === 'New' ? NEW_ORG_COLUMNS : EXISTING_ORG_COLUMNS;
}

/**
 * Display label for a column named in a CsvParseError. Falls back to the raw key for
 * anything not in the map, and returns null for the file-level errors that carry no
 * column at all (empty file, row-cap exceeded) so callers can omit the label entirely
 * rather than rendering a bare ": ".
 */
export function columnLabel(column: string): string | null {
  if (column === '') return null;
  return COLUMN_DISPLAY_NAMES[column as CsvColumnKey] ?? column;
}

/**
 * Whether a column's header was found in the uploaded CSV.
 *
 * - `matched` — the header is present.
 * - `missing` — absent, and the backend reported it as a required column. An error.
 * - `absent` — absent but not required, e.g. euid omitted by an admin. Not an error.
 * - `unknown` — only for a result persisted before the API returned matchedColumns;
 *   presence is then inferred from row values, which cannot distinguish an absent
 *   column from one present and empty in every row.
 */
export type ColumnMatchStatus = 'matched' | 'missing' | 'absent' | 'unknown';

export function buildColumnStatuses(
  columns: readonly CsvColumnKey[],
  result: ParsedCsvResult,
  fileErrors: readonly CsvParseError[],
): Record<string, ColumnMatchStatus> {
  const required = new Set(fileErrors.filter((e) => e.column !== '').map((e) => e.column));
  const matched = result.matchedColumns;

  const statuses: Record<string, ColumnMatchStatus> = {};
  for (const col of columns) {
    if (matched !== undefined) {
      statuses[col] = matched.includes(col)
        ? 'matched'
        : required.has(col)
          ? 'missing'
          : 'absent';
      continue;
    }

    // Legacy fallback for a CSV parsed before matchedColumns existed.
    if (required.has(col)) {
      statuses[col] = 'missing';
      continue;
    }

    const prop = COLUMN_TO_ROW_PROP[col];
    const hasValue = result.rows.some((row) => {
      const value = row[prop];
      return value !== null && value !== undefined && String(value).trim() !== '';
    });
    statuses[col] = hasValue ? 'matched' : 'unknown';
  }

  return statuses;
}
